import { Carriers } from "@redotech/redo-fulfillment-carrier-common/carrier-service-enums";
import { RedoCheckbox } from "@redotech/redo-web/arbiter-components/checkbox/redo-checkbox";
import { RedoSingleDatePicker } from "@redotech/redo-web/arbiter-components/date-picker/redo-single-date-picker";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import {
  RedoTable,
  RedoTableLayout,
} from "@redotech/redo-web/arbiter-components/tables/redo-table";
import {
  RedoTablePaddedCell,
  RedoTableTextCell,
} from "@redotech/redo-web/arbiter-components/tables/redo-table-cells";
import { StandardRedoTableHeaderCell } from "@redotech/redo-web/arbiter-components/tables/redo-table-header-cells";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { DisabledTooltip } from "@redotech/redo-web/tooltip/disabled-tooltip";
import {
  dateToPlainDateTime,
  plainDateTimeToDate,
} from "@redotech/util/temporal";
import { memo } from "react";
import {
  canNotifyMarketplace,
  FulfillmentModalRowInput,
  MarkAsFulfilledForm,
} from "./mark-as-fulfilled-form";
import { carrierOptions } from "./mark-as-fulfilled-modal";

interface TableRow {
  data: FulfillmentModalRowInput;
  doUpdate: (update: Partial<FulfillmentModalRowInput>) => void;
}

const markAsFulfilledTableLayout: RedoTableLayout<TableRow> = {
  columns: [
    {
      key: "order-name",
      widthMode: { type: "fixed", width: 120 },
      renderHeaderCell: () => (
        <StandardRedoTableHeaderCell headerText="Order" />
      ),
      renderNormalCell: ({ row }) => {
        return <RedoTableTextCell text={row.data.orderName} />;
      },
    },
    {
      key: "customer-name",
      widthMode: { type: "fill" },
      renderHeaderCell: () => (
        <StandardRedoTableHeaderCell headerText="Customer" />
      ),
      renderNormalCell: ({ row }) => {
        return (
          <RedoTablePaddedCell>
            <Flex dir="column" gap="xs">
              <Text fontSize="xs" fontWeight="regular" textColor="secondary">
                {row.data.customerName}
              </Text>
            </Flex>
          </RedoTablePaddedCell>
        );
      },
    },
    {
      key: "ship-date",
      widthMode: { type: "fixed", width: 150 },
      renderHeaderCell: () => (
        <StandardRedoTableHeaderCell headerText="Ship date" />
      ),
      renderNormalCell: ({ row }) => {
        return (
          <RedoTablePaddedCell>
            <RedoSingleDatePicker
              buttonSize="sm"
              date={dateToPlainDateTime(row.data.shipDate).toPlainDate()}
              dateStringOptions={{ year: undefined }}
              maxDate={dateToPlainDateTime(new Date()).toPlainDate()}
              setDate={(plainDate) => {
                row.doUpdate({
                  shipDate: plainDateTimeToDate(plainDate.toPlainDateTime()),
                });
              }}
            />
          </RedoTablePaddedCell>
        );
      },
    },
    {
      key: "shipping-selection",
      widthMode: { type: "fill" },
      renderHeaderCell: () => (
        <StandardRedoTableHeaderCell headerText="Shipping selection" />
      ),
      renderNormalCell: ({ row }) => {
        return (
          <RedoTablePaddedCell>
            <RedoSingleSelectDropdownInput<Carriers>
              disabled={false}
              options={carrierOptions}
              optionSelected={({ item: carrier }) => {
                row.doUpdate({ shippingSelection: carrier.value });
              }}
              placeholder="Select carrier"
              selectedItem={carrierOptions.find(
                (carrier) => carrier.value === row.data.shippingSelection,
              )}
              size="xs"
            />
          </RedoTablePaddedCell>
        );
      },
    },
    {
      key: "tracking-number",
      widthMode: { type: "fixed", width: 230 },
      renderHeaderCell: () => (
        <StandardRedoTableHeaderCell headerText="Tracking number" />
      ),
      renderNormalCell: ({ row }) => {
        return (
          <RedoTablePaddedCell>
            <RedoTextInput
              placeholder="Enter tracking number"
              setValue={(value) => {
                row.doUpdate({ trackingNumber: value.trim() });
              }}
              value={row.data.trackingNumber}
            />
          </RedoTablePaddedCell>
        );
      },
    },
    {
      key: "notify-customer",
      widthMode: { type: "fixed", width: 120 },
      renderHeaderCell: () => (
        <StandardRedoTableHeaderCell headerText="Notify customer" />
      ),
      renderNormalCell: ({ row }) => {
        return (
          <RedoTablePaddedCell>
            <Flex align="center" justify="center">
              <DisabledTooltip
                disabledMessage={
                  !row.data.notifyMarketplace
                    ? "Cannot notify customer without notifying marketplace"
                    : undefined
                }
              >
                <RedoCheckbox
                  disabled={!row.data.notifyMarketplace}
                  setValue={(value) => {
                    row.doUpdate({ notifyCustomer: !!value });
                  }}
                  size="xs"
                  value={row.data.notifyCustomer}
                />
              </DisabledTooltip>
            </Flex>
          </RedoTablePaddedCell>
        );
      },
    },
    {
      key: "notify-marketplace",
      widthMode: { type: "fixed", width: 150 },
      renderHeaderCell: () => (
        <StandardRedoTableHeaderCell headerText="Notify marketplace" />
      ),
      renderNormalCell: ({ row }) => {
        const disabled = !canNotifyMarketplace(row.data);
        return (
          <RedoTablePaddedCell>
            <Flex align="center" justify="center">
              <DisabledTooltip
                disabledMessage={
                  disabled
                    ? "Cannot notify marketplace without tracking number and shipping selection"
                    : undefined
                }
              >
                <RedoCheckbox
                  disabled={disabled}
                  setValue={(value) => {
                    row.doUpdate({ notifyMarketplace: !!value });
                  }}
                  size="xs"
                  value={row.data.notifyMarketplace}
                />
              </DisabledTooltip>
            </Flex>
          </RedoTablePaddedCell>
        );
      },
    },
  ],
};

export const MarkAsFulfilledModalTable = memo(
  function MarkAsFulfilledModalTable({
    form,
    doUpdate,
  }: {
    form: MarkAsFulfilledForm;
    doUpdate: (
      index: number,
      update: Partial<FulfillmentModalRowInput>,
    ) => void;
  }) {
    return (
      <Flex w="full">
        <RedoTable
          layout={markAsFulfilledTableLayout}
          rowsOfData={form.value.rowData.map((row, index) => ({
            data: row,
            doUpdate: (update: Partial<FulfillmentModalRowInput>) =>
              doUpdate(index, update),
          }))}
        />
      </Flex>
    );
  },
);
